import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, PrinterIcon, Download, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useFormQuery, useFormFieldsQuery } from "../api/queries";
import { FormPreviewContent } from "../ui/FormPreviewContent";
import type { FormDTO } from "../../domain/contracts";
import { FormPreviewTour, useFormPreviewTour } from "../tour/FormPreviewTour";

export function FormPreviewPage() {
  const { id } = useParams({ from: "/app/forms/$id/preview" });
  const nav = useNavigate({ from: "/app/forms/$id/preview" });

  const { data: form, isLoading, error } = useFormQuery(id ?? "");
  const formData = form as FormDTO | undefined;

  // Fetch form fields for preview
  const { data: fieldsData } = useFormFieldsQuery(
    id ?? "",
    formData?.currentVersionId ?? null,
  );

  // Tour
  const tour = useFormPreviewTour();

  const handleBack = () => {
    nav({ to: "/app/forms/$id", params: { id: id ?? "" } });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    if (!formData) return;

    const sanitizedFormName = formData.name
      .replace(/[^a-zA-Z0-9\u0E80-\u0EFF\s]/g, "")
      .trim()
      .replace(/\s+/g, "_");

    // Set document title for PDF filename
    const originalTitle = document.title;
    document.title = `${sanitizedFormName || "Form"}.pdf`;

    // Trigger print dialog
    window.print();

    // Restore original title after a delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !formData) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບຟອມ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ຟອມທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ຟອມ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <FormPreviewTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        {/* Header Actions - Hidden in print */}
        <div className="mb-6 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="gap-2"
              data-tourid="preview-back-button"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປທີ່ຟອມ
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>

          <div className="flex items-center gap-2" data-tourid="preview-actions">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
            <Button variant="outline" onClick={handleExportPDF} className="gap-2">
              <Download className="h-4 w-4" />
              ສົ່ງອອກ PDF
            </Button>
          </div>
        </div>

        {/* PDF Preview Content */}
        {fieldsData?.fields && (
          <div data-tourid="preview-content">
            <FormPreviewContent
              formName={formData.name}
              fields={(fieldsData.fields as Array<{
                fieldType: string;
                label: string;
                isRequired: boolean;
                hint: string | null;
                orderIndex: number;
                options: unknown;
                settings: unknown;
              }>).map((f) => ({
                id: `field-${f.orderIndex}`,
                type: f.fieldType as "text" | "textarea" | "number" | "date" | "time" | "datetime" | "email" | "phone" | "select" | "multiselect" | "checkbox" | "radio" | "file" | "signature",
                label: f.label,
                required: f.isRequired,
                placeholder: f.hint || undefined,
                order: f.orderIndex,
                config: {
                  options: Array.isArray(f.options) ? f.options : undefined,
                  ...(typeof f.settings === "object" && f.settings ? f.settings : {}),
                },
              }))}
              description={formData.description}
              categoryName={formData.categoryName}
              departmentName={formData.departmentName}
            />
          </div>
        )}

        {(!fieldsData?.fields || fieldsData.fields.length === 0) && (
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground">ຍັງບໍ່ມີຊ່ອງຂໍ້ມູນ</p>
            </div>
          </div>
        )}
      </Main>
    </>
  );
}
